export interface HeroVideo {
  id: string;
  name: string;
  source: "upload" | "embed";
  video_url: string | null;
  provider: "youtube" | "vimeo" | null;
  video_id: string | null;
  poster_url: string | null;
  caption: string | null;
  active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  subtitles_vtt?: string | null;
  subtitles_default_on?: boolean;
  allow_download?: boolean;
  allow_pip?: boolean;
  allow_playback_rate?: boolean;
  allow_remote_playback?: boolean;
}

/** Reads a YouTube or Vimeo link. Returns null for anything else. */
export function parseEmbedUrl(input: string): { provider: "youtube" | "vimeo"; id: string } | null {
  let url: URL;
  try {
    url = new URL(input.trim());
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^www\.|^m\./, "");
  if (host === "youtu.be") {
    const id = url.pathname.slice(1).split("/")[0];
    return /^[\w-]{11}$/.test(id) ? { provider: "youtube", id } : null;
  }
  if (host === "youtube.com" || host === "youtube-nocookie.com") {
    const v = url.searchParams.get("v");
    const fromPath = url.pathname.match(/^\/(?:embed|shorts|live)\/([\w-]{11})/)?.[1];
    const id = v ?? fromPath;
    return id && /^[\w-]{11}$/.test(id) ? { provider: "youtube", id } : null;
  }
  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const id = url.pathname.match(/(\d{6,})/)?.[1];
    return id ? { provider: "vimeo", id } : null;
  }
  return null;
}

export function embedSrc(provider: "youtube" | "vimeo", id: string): string {
  return provider === "youtube"
    ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`
    : `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1`;
}

/** The video to show: master switch on, one active row, inside its date window, playable. */
export function pickHeroVideo(enabled: boolean, rows: HeroVideo[], now: Date = new Date()): HeroVideo | null {
  if (!enabled) return null;
  const t = now.getTime();
  return (
    rows.find((r) => {
      if (!r.active) return false;
      if (r.starts_at && new Date(r.starts_at).getTime() > t) return false;
      if (r.ends_at && new Date(r.ends_at).getTime() <= t) return false;
      if (r.source === "upload") return !!r.video_url;
      return !!r.provider && !!r.video_id;
    }) ?? null
  );
}

export interface HeroVideoControls {
  allow_download?: boolean;
  allow_playback_rate?: boolean;
  allow_remote_playback?: boolean;
}

/** Value for the video element's controlsList; empty when every control is allowed. */
export function controlsListFor(v: HeroVideoControls): string {
  const parts: string[] = [];
  if (v.allow_download === false) parts.push("nodownload");
  if (v.allow_playback_rate === false) parts.push("noplaybackrate");
  if (v.allow_remote_playback === false) parts.push("noremoteplayback");
  return parts.join(" ");
}

/** Converts SRT subtitles to WebVTT. VTT input is returned unchanged. */
export function srtToVtt(input: string): string {
  const text = input.replace(/\r\n?/g, "\n").trim();
  if (text.startsWith("WEBVTT")) return text + "\n";
  const body = text
    .split(/\n{2,}/)
    .map((block) => block.replace(/^\d+\n(?=\d{2}:)/, ""))
    .map((block) => block.replace(/(\d{2}:\d{2}:\d{2}),(\d{3})/g, "$1.$2"))
    .join("\n\n");
  return `WEBVTT\n\n${body}\n`;
}
